import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, FileSignature, Check, AlertTriangle, Loader2, Download, ArrowLeft, Fingerprint, ExternalLink, PenTool } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SignaturePad } from "../components/SignaturePad";

const AgreementBody = ({ body }) => (
  <div className="max-h-52 overflow-y-auto pr-3 space-y-2 border border-white/10 rounded-sm p-4 bg-black/30">
    {(body || "").split("\n").filter((l) => l.trim()).map((line, i) => {
      const isHead = line.trim().startsWith("## ");
      return isHead ? (
        <div key={i} className="font-grotesk text-[11px] uppercase tracking-[0.14em] text-[#B87333] pt-1">{line.replace("## ", "")}</div>
      ) : (
        <p key={i} className="font-body text-[13px] text-[#C9C9C2] leading-relaxed">{line}</p>
      );
    })}
  </div>
);

export default function KycVerify() {
  const { user, api, refresh } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [zoho, setZoho] = useState({ configured: false, credentials_present: false });
  const [providers, setProviders] = useState([]); // [{key,label,description,available}]
  const [method, setMethod] = useState("zoho"); // "zoho" | "sandbox" | "in_house"
  const [pan, setPan] = useState("");
  const [gstin, setGstin] = useState("");
  const [sigs, setSigs] = useState({}); // key -> {type, data, consent}
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [zohoReqs, setZohoReqs] = useState([]); // [{agreement_key, request_id, action_id, embed_url, status}]
  const [activeZoho, setActiveZoho] = useState(null); // request currently in iframe
  const [polling, setPolling] = useState(false);
  const [zohoError, setZohoError] = useState(null); // {message, code?}
  // Sandbox provider state
  const [sandboxReqs, setSandboxReqs] = useState([]);   // [{agreement_key, request_id, signing_url, status}]
  const [sandboxError, setSandboxError] = useState(null);
  const [sandboxPolling, setSandboxPolling] = useState(false);
  const sandboxPollRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, ags, zc, pv] = await Promise.all([
        api.get("/kyc/status"),
        api.get("/kyc/agreements"),
        api.get("/kyc/zoho/config").catch(() => ({ data: { configured: false, credentials_present: false } })),
        api.get("/kyc/providers").catch(() => ({ data: { providers: [] } })),
      ]);
      setStatus(st.data);
      setAgreements(ags.data);
      setZoho(zc.data);
      setProviders(pv.data?.providers || []);
      // Default selection order — Zoho > Sandbox > In-house
      const zohoOn = zc.data?.configured;
      const sandboxOn = (pv.data?.providers || []).find((p) => p.key === "sandbox")?.available;
      setMethod(zohoOn ? "zoho" : sandboxOn ? "sandbox" : "in_house");
    } catch { toast.error("Could not load verification details."); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user?.profile) {
      setPan((p) => p || user.profile.pan || "");
      setGstin((g) => g || user.profile.gst_number || "");
    }
  }, [user]);

  const setSig = (key, val) => setSigs((s) => ({ ...s, [key]: { ...s[key], ...val } }));

  // --- In-house path ---
  const submitInHouse = async () => {
    if (!pan.trim() || !gstin.trim()) { toast.error("Enter your PAN and GSTIN for eKYC."); return; }
    for (const a of agreements) {
      const s = sigs[a.key];
      if (!s?.consent) { toast.error(`Please tick consent for "${a.title}".`); return; }
      if (!s?.data?.trim()) { toast.error(`Please sign "${a.title}".`); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        pan: pan.trim().toUpperCase(),
        gstin: gstin.trim().toUpperCase(),
        signatures: agreements.map((a) => ({
          agreement_key: a.key,
          type: sigs[a.key].type || "type",
          data: sigs[a.key].data,
          consent: !!sigs[a.key].consent,
        })),
      };
      const { data } = await api.post("/kyc/submit", payload);
      setResult(data);
      if (data.verified) {
        toast.success("Verified! Your account is now marked Verified.");
        await refresh(); await load();
      } else {
        toast.error(data.message || "eKYC checks failed. Please re-check your PAN/GSTIN.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  // --- Zoho path ---
  const startZoho = async () => {
    if (!pan.trim() || !gstin.trim()) { toast.error("Enter your PAN and GSTIN for eKYC."); return; }
    setZohoError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/kyc/zoho/start", {
        pan: pan.trim().toUpperCase(),
        gstin: gstin.trim().toUpperCase(),
      });
      setZohoReqs(data.requests || []);
      const firstPending = (data.requests || []).find((r) => r.status !== "completed");
      setActiveZoho(firstPending || null);
      if (firstPending) toast.success("Zoho Sign is ready. Sign each document in turn.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Could not start Zoho signing.";
      setZohoError({ message: msg, status: e?.response?.status });
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const refreshEmbed = async (req) => {
    setZohoError(null);
    try {
      const { data } = await api.get(`/kyc/zoho/embed/${req.request_id}/${req.action_id}`);
      const updated = { ...req, embed_url: data.embed_url };
      setZohoReqs((rs) => rs.map((r) => (r.request_id === req.request_id ? updated : r)));
      setActiveZoho(updated);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Could not refresh signing link.";
      setZohoError({ message: msg, status: e?.response?.status });
      toast.error(msg);
    }
  };

  const pollFinalize = useCallback(async () => {
    try {
      const { data } = await api.post("/kyc/zoho/finalize");
      setZohoReqs(data.requests || []);
      if (data.verified_now) {
        toast.success("All agreements signed — account Verified.");
        await refresh(); await load();
      } else {
        const nextPending = (data.requests || []).find((r) => r.status !== "completed" && r.status !== "declined");
        if (nextPending && (!activeZoho || activeZoho.request_id !== nextPending.request_id)) {
          setActiveZoho(nextPending);
        }
      }
    } catch { /* silent */ }
  }, [api, refresh, load, activeZoho]);

  // Poll every 8s while any Zoho request is in progress
  useEffect(() => {
    if (!zohoReqs.length) return;
    const pending = zohoReqs.some((r) => r.status === "in_progress");
    if (!pending) return;
    setPolling(true);
    pollRef.current = setInterval(pollFinalize, 8000);
    return () => { clearInterval(pollRef.current); setPolling(false); };
  }, [zohoReqs, pollFinalize]);

  // Detect return from Zoho redirect
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("zoho")) {
      pollFinalize();
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [pollFinalize]);

  // ---- Sandbox eSign path ----
  const startSandbox = async () => {
    if (!pan.trim() || !gstin.trim()) { toast.error("Enter your PAN and GSTIN for eKYC."); return; }
    setSandboxError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/kyc/sandbox/start", {
        pan: pan.trim().toUpperCase(), gstin: gstin.trim().toUpperCase(),
      });
      setSandboxReqs(data.requests || []);
      toast.success("Sandbox eSign is ready. Open each document and sign with Aadhaar.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Could not start Sandbox eSign.";
      setSandboxError({ message: msg, status: e?.response?.status });
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const pollSandboxFinalize = useCallback(async () => {
    try {
      const { data } = await api.post("/kyc/sandbox/finalize");
      setSandboxReqs(data.requests || []);
      if (data.verified_now) {
        toast.success("All agreements signed — account verified via Sandbox.");
        await refresh(); await load();
      }
    } catch { /* silent */ }
  }, [api, refresh, load]);

  // Poll every 8s while any Sandbox request is in progress
  useEffect(() => {
    if (!sandboxReqs.length) return;
    const pending = sandboxReqs.some((r) => r.status === "in_progress");
    if (!pending) return;
    setSandboxPolling(true);
    sandboxPollRef.current = setInterval(pollSandboxFinalize, 8000);
    return () => { clearInterval(sandboxPollRef.current); setSandboxPolling(false); };
  }, [sandboxReqs, pollSandboxFinalize]);

  // Detect return from Sandbox redirect
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("provider") === "sandbox") {
      setMethod("sandbox");
      pollSandboxFinalize();
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [pollSandboxFinalize]);

  const isVerified = status?.kyc_status === "verified";
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] px-6 md:px-12 py-16">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="max-w-[900px] mx-auto">
        <Link to="/account" className="inline-flex items-center gap-2 font-grotesk text-[11px] uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#B87333] transition-colors mb-8">
          <ArrowLeft size={14} /> Back to account
        </Link>

        <span className="overline text-[#B87333]">/ eKYC &amp; Digital Signature</span>
        <h1 className="font-serif text-4xl md:text-5xl mt-4">Verify your account.</h1>
        <p className="font-body text-[#8A8A82] max-w-xl mt-3 leading-relaxed">
          Complete a quick identity check and sign the agreements below. On success your account is
          marked <span className="text-[#2FB999]">Verified</span> and unlocks full tier access.
        </p>

        {loading ? (
          <p className="font-body text-[#8A8A82] mt-12 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p>
        ) : isVerified ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="kyc-verified-panel"
            className="mt-10 rounded-sm border border-[#2FB999]/40 bg-[#2FB999]/[0.06] p-8">
            <div className="flex items-center gap-3">
              <ShieldCheck className="text-[#2FB999]" size={28} />
              <div>
                <div className="font-serif text-2xl text-[#2FB999]">Account Verified</div>
                <p className="font-body text-sm text-[#C9C9C2] mt-1">
                  eKYC passed and all agreements signed{status?.verified_at ? ` on ${new Date(status.verified_at).toLocaleDateString()}` : ""}.
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="overline text-[#8A8A82] mb-3">Your signed agreements</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {(status.signed_keys || []).map((k) => {
                  const ag = (status.required || []).find((a) => a.key === k) || { title: k };
                  const zRec = zohoReqs.find((r) => r.agreement_key === k);
                  const href = zRec?.object_path
                    ? `${backend}/api/kyc/zoho/signed/${zRec.request_id}.pdf`
                    : `${backend}/api/kyc/agreements/${k}/signed.pdf`;
                  return (
                    <a key={k} href={href} target="_blank" rel="noreferrer"
                      data-testid={`download-signed-${k}`}
                      className="flex items-center justify-between gap-3 rounded-sm border border-white/10 px-4 py-3 hover:border-[#B87333]/60 transition-colors">
                      <span className="font-body text-sm text-[#C9C9C2]">{ag.title}</span>
                      <Download size={15} className="text-[#B87333]" />
                    </a>
                  );
                })}
              </div>
            </div>
            <Link to="/account" data-testid="kyc-go-account" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">
              Go to account
            </Link>
          </motion.div>
        ) : (
          <div className="mt-10 space-y-8" data-testid="kyc-form">
            {/* Method picker — two provider buttons + fallback */}
            {(() => {
              const zohoOn = !!zoho.configured;
              const sandboxOn = !!(providers.find((p) => p.key === "sandbox")?.available);
              const anyProviderOn = zohoOn || sandboxOn;
              return (
                <section className="rounded-sm border border-white/10 p-6 md:p-7" data-testid="signing-method-picker">
                  <div className="overline text-[#8A8A82]">Choose your signing method</div>
                  <p className="font-body text-sm text-[#C9C9C2] mt-1">
                    Whichever you pick, the final result is the same — a signed PDF stored in your Vault and your account status moves to <span className="text-[#B87333]">Awaiting Approval</span>.
                  </p>
                  <div className="mt-5 grid md:grid-cols-2 gap-4" role="radiogroup">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={method === "zoho"}
                      data-testid="method-zoho"
                      disabled={!zohoOn}
                      onClick={() => setMethod("zoho")}
                      className={`text-left rounded-sm border p-5 transition-colors ${method === "zoho" && zohoOn ? "border-[#B87333] bg-[#B87333]/[0.08]" : "border-white/10 hover:border-white/25"} disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="grid place-items-center w-10 h-10 rounded-sm border border-[#B87333]/50 text-[#B87333] shrink-0"><PenTool size={18} /></span>
                        <div className="min-w-0">
                          <div className="font-serif text-xl text-[#F5F5F0]">Zoho Sign</div>
                          <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82]">
                            Enterprise · iframe · Instant
                          </div>
                        </div>
                        {!zohoOn && (
                          <span className="ml-auto font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] border border-white/20 rounded-full px-2 py-1">
                            Not connected
                          </span>
                        )}
                      </div>
                      <p className="font-body text-xs text-[#C9C9C2] mt-3 leading-relaxed">
                        Sign inline in a secure Zoho embed. Certified PDF is stored the moment you finish.
                      </p>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={method === "sandbox"}
                      data-testid="method-sandbox"
                      disabled={!sandboxOn}
                      onClick={() => setMethod("sandbox")}
                      className={`text-left rounded-sm border p-5 transition-colors ${method === "sandbox" && sandboxOn ? "border-[#A78BFA] bg-[#A78BFA]/[0.08]" : "border-white/10 hover:border-white/25"} disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="grid place-items-center w-10 h-10 rounded-sm border border-[#A78BFA]/50 text-[#A78BFA] shrink-0"><Fingerprint size={18} /></span>
                        <div className="min-w-0">
                          <div className="font-serif text-xl text-[#F5F5F0]">Sandbox eSign</div>
                          <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82]">
                            Aadhaar OTP · Regulator-Compliant
                          </div>
                        </div>
                        {!sandboxOn && (
                          <span className="ml-auto font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] border border-white/20 rounded-full px-2 py-1">
                            Not configured
                          </span>
                        )}
                      </div>
                      <p className="font-body text-xs text-[#C9C9C2] mt-3 leading-relaxed">
                        India CCA-compliant Aadhaar eSign by sandbox.co.in. You'll be redirected to complete OTP verification.
                      </p>
                    </button>
                  </div>
                  {anyProviderOn && (
                    <button
                      type="button"
                      data-testid="method-in-house"
                      onClick={() => setMethod("in_house")}
                      className={`mt-4 font-grotesk text-[10px] uppercase tracking-[0.2em] ${method === "in_house" ? "text-[#B87333]" : "text-[#8A8A82] hover:text-[#B87333]"}`}
                    >
                      Or use the fallback Draw / Type signature →
                    </button>
                  )}
                  {!anyProviderOn && (
                    <div className="mt-4 rounded-sm border border-[#B87333]/30 bg-[#B87333]/[0.05] p-3 font-body text-xs text-[#C9C9C2]">
                      No eSign provider is connected yet. An admin must configure Zoho Sign or Sandbox eSign first. Meanwhile, use the Draw/Type fallback below.
                    </div>
                  )}
                </section>
              );
            })()}

            {/* eKYC */}
            <section className="rounded-sm border border-white/10 p-7">
              <div className="flex items-center gap-2 mb-5">
                <Fingerprint size={18} className="text-[#B87333]" />
                <h2 className="font-serif text-2xl">Step 1 · Identity (eKYC)</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <label className="block">
                  <span className="overline text-[#8A8A82]">PAN</span>
                  <input data-testid="kyc-pan" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-2.5 mt-1 font-body text-[#F5F5F0] tracking-wider" />
                </label>
                <label className="block">
                  <span className="overline text-[#8A8A82]">GSTIN</span>
                  <input data-testid="kyc-gstin" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-2.5 mt-1 font-body text-[#F5F5F0] tracking-wider" />
                </label>
              </div>
              {result && !result.verified && (
                <div data-testid="kyc-failed-checks" className="mt-5 rounded-sm border border-[#C0392B]/40 bg-[#C0392B]/10 p-4 space-y-1.5">
                  <div className="flex items-center gap-2 text-[#E08A7E] font-grotesk text-[11px] uppercase tracking-[0.18em]"><AlertTriangle size={14} /> Verification failed</div>
                  {result.ekyc?.checks?.map((c, i) => (
                    <div key={i} className="flex items-center justify-between font-body text-sm">
                      <span className="text-[#C9C9C2]">{c.label}</span>
                      <span style={{ color: c.ok ? "#2FB999" : "#E08A7E" }}>{c.ok ? "Pass" : c.detail}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Zoho signing path */}
            {method === "zoho" && zoho.configured ? (
              <section className="rounded-sm border border-white/10 p-7" data-testid="zoho-panel">
                <div className="flex items-center gap-2 mb-2">
                  <PenTool size={18} className="text-[#B87333]" />
                  <h2 className="font-serif text-2xl">Step 2 · Sign with Zoho eSign</h2>
                </div>
                <p className="font-body text-sm text-[#8A8A82] mb-6">
                  Each agreement opens inside a secure Zoho signing panel. A signed &amp; certified PDF is stored the moment you finish.
                </p>

                {zohoError && (
                  <div role="alert" data-testid="zoho-error-alert"
                    className="mb-5 rounded-sm border border-[#C0392B] bg-[#C0392B]/15 p-4 flex items-start gap-3">
                    <AlertTriangle size={18} className="text-[#E08A7E] shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-grotesk text-[11px] uppercase tracking-[0.18em] text-[#E08A7E]">Zoho Sign error{zohoError.status ? ` · ${zohoError.status}` : ""}</div>
                      <div className="font-body text-sm text-[#F5F5F0] mt-1 leading-relaxed" data-testid="zoho-error-message">{zohoError.message}</div>
                    </div>
                    <button data-testid="zoho-error-dismiss" onClick={() => setZohoError(null)}
                      className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#C9C9C2] hover:text-[#F5F5F0] shrink-0 pt-0.5">
                      Dismiss
                    </button>
                  </div>
                )}

                {!zohoReqs.length ? (
                  <button data-testid="zoho-start" onClick={startZoho} disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">
                    {submitting ? <><Loader2 className="animate-spin" size={15} /> Preparing…</> : <><FileSignature size={15} /> Start Zoho eSign</>}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <ol className="space-y-2">
                      {zohoReqs.map((r, i) => {
                        const done = r.status === "completed";
                        const declined = r.status === "declined";
                        return (
                          <li key={r.request_id} data-testid={`zoho-req-${r.agreement_key}`}
                            className={`flex items-center justify-between gap-3 rounded-sm border p-3 ${done ? "border-[#2FB999]/40 bg-[#2FB999]/[0.05]" : declined ? "border-[#C0392B]/40" : "border-white/10"}`}>
                            <div className="flex items-center gap-3">
                              <span className="font-grotesk text-[11px] text-[#8A8A82] tabular-nums w-6">{String(i + 1).padStart(2, "0")}</span>
                              <span className="font-body text-sm text-[#C9C9C2]">{r.agreement_title}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {done ? (
                                <span className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#2FB999]"><Check size={12} className="inline mr-1" />Signed</span>
                              ) : declined ? (
                                <span className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#E08A7E]">Declined</span>
                              ) : (
                                <button data-testid={`zoho-sign-${r.agreement_key}`} onClick={() => refreshEmbed(r)}
                                  className="font-grotesk text-[10px] uppercase tracking-[0.16em] rounded-full border border-[#B87333] text-[#B87333] px-3 py-1.5 hover:bg-[#B87333] hover:text-black transition-colors inline-flex items-center gap-1.5">
                                  Sign now <ExternalLink size={11} />
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>

                    {activeZoho?.embed_url && activeZoho.status !== "completed" && (
                      <div className="rounded-sm border border-[#B87333]/40 p-3 bg-black/40">
                        <div className="flex items-center justify-between mb-2">
                          <span className="overline text-[#B87333]">Signing: {activeZoho.agreement_title}</span>
                          <div className="flex items-center gap-2">
                            <a href={activeZoho.embed_url} target="_blank" rel="noreferrer"
                              className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#C9C9C2] hover:text-[#B87333] inline-flex items-center gap-1">
                              Open in new tab <ExternalLink size={11} />
                            </a>
                          </div>
                        </div>
                        <iframe title="Zoho Sign" src={activeZoho.embed_url}
                          className="w-full h-[560px] rounded-sm bg-white" data-testid="zoho-iframe" />
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <button data-testid="zoho-refresh" onClick={pollFinalize}
                        className="font-grotesk text-[11px] uppercase tracking-[0.18em] text-[#8A8A82] hover:text-[#B87333] inline-flex items-center gap-2">
                        {polling ? <Loader2 className="animate-spin" size={12} /> : null}
                        I've finished signing · refresh
                      </button>
                    </div>
                  </div>
                )}
              </section>
            ) : method === "sandbox" && providers.find((p) => p.key === "sandbox")?.available ? (
              <section className="rounded-sm border border-white/10 p-7" data-testid="sandbox-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Fingerprint size={18} className="text-[#A78BFA]" />
                  <h2 className="font-serif text-2xl">Step 2 · Sign with Sandbox eSign</h2>
                </div>
                <p className="font-body text-sm text-[#8A8A82] mb-6">
                  You'll be redirected to Sandbox's secure signing page. Complete Aadhaar OTP verification, then you'll bounce back here — the signed PDF lands in your Vault automatically and your status flips to <span className="text-[#B87333]">Awaiting Approval</span>.
                </p>

                {sandboxError && (
                  <div role="alert" data-testid="sandbox-error-alert"
                    className="mb-5 rounded-sm border border-[#C0392B] bg-[#C0392B]/15 p-4 flex items-start gap-3">
                    <AlertTriangle size={18} className="text-[#E08A7E] shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-grotesk text-[11px] uppercase tracking-[0.18em] text-[#E08A7E]">
                        Sandbox eSign error{sandboxError.status ? ` · ${sandboxError.status}` : ""}
                      </div>
                      <div className="font-body text-sm text-[#F5F5F0] mt-1 leading-relaxed" data-testid="sandbox-error-message">{sandboxError.message}</div>
                    </div>
                    <button data-testid="sandbox-error-dismiss" onClick={() => setSandboxError(null)}
                      className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#C9C9C2] hover:text-[#F5F5F0] shrink-0 pt-0.5">
                      Dismiss
                    </button>
                  </div>
                )}

                {!sandboxReqs.length ? (
                  <button data-testid="sandbox-start" onClick={startSandbox} disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#A78BFA] transition-colors disabled:opacity-50">
                    {submitting ? <><Loader2 className="animate-spin" size={15} /> Preparing…</> : <><FileSignature size={15} /> Start Sandbox eSign</>}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <ol className="space-y-2">
                      {sandboxReqs.map((r, i) => {
                        const done = r.status === "completed";
                        const declined = r.status === "declined";
                        return (
                          <li key={r.request_id} data-testid={`sandbox-req-${r.agreement_key}`}
                            className={`flex items-center justify-between gap-3 rounded-sm border p-3 ${done ? "border-[#2FB999]/40 bg-[#2FB999]/[0.05]" : declined ? "border-[#C0392B]/40" : "border-white/10"}`}>
                            <div className="flex items-center gap-3">
                              <span className="font-grotesk text-[11px] text-[#8A8A82] tabular-nums w-6">{String(i + 1).padStart(2, "0")}</span>
                              <span className="font-body text-sm text-[#C9C9C2]">{r.agreement_title}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {done ? (
                                <span className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#2FB999]"><Check size={12} className="inline mr-1" />Signed</span>
                              ) : declined ? (
                                <span className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#E08A7E]">Declined</span>
                              ) : (
                                <a data-testid={`sandbox-sign-${r.agreement_key}`}
                                  href={r.signing_url} target="_blank" rel="noreferrer"
                                  className="font-grotesk text-[10px] uppercase tracking-[0.16em] rounded-full border border-[#A78BFA] text-[#A78BFA] px-3 py-1.5 hover:bg-[#A78BFA] hover:text-black transition-colors inline-flex items-center gap-1.5">
                                  Sign with Aadhaar <ExternalLink size={11} />
                                </a>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>

                    <div className="flex items-center gap-3">
                      <button data-testid="sandbox-refresh" onClick={pollSandboxFinalize}
                        className="font-grotesk text-[11px] uppercase tracking-[0.18em] text-[#8A8A82] hover:text-[#A78BFA] inline-flex items-center gap-2">
                        {sandboxPolling ? <Loader2 className="animate-spin" size={12} /> : null}
                        I've finished signing · refresh
                      </button>
                    </div>
                  </div>
                )}
              </section>
            ) : (
              /* In-house path */
              <>
                <section className="rounded-sm border border-white/10 p-7">
                  <div className="flex items-center gap-2 mb-2">
                    <FileSignature size={18} className="text-[#B87333]" />
                    <h2 className="font-serif text-2xl">Step 2 · Sign agreements</h2>
                  </div>
                  <p className="font-body text-sm text-[#8A8A82] mb-6">Read each agreement, tick consent and add your signature.</p>
                  <div className="space-y-6">
                    {agreements.map((a) => {
                      const s = sigs[a.key] || {};
                      return (
                        <div key={a.key} data-testid={`agreement-${a.key}`} className="rounded-sm border border-white/10 p-5">
                          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                            <h3 className="font-serif text-xl">{a.title}</h3>
                            <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{a.category}</span>
                          </div>
                          <AgreementBody body={a.body} />
                          <label className="flex items-start gap-3 mt-4 cursor-pointer">
                            <input type="checkbox" data-testid={`consent-${a.key}`} checked={!!s.consent}
                              onChange={(e) => setSig(a.key, { consent: e.target.checked })}
                              className="mt-1 accent-[#B87333] w-4 h-4" />
                            <span className="font-body text-sm text-[#C9C9C2]">I have read and agree to the terms of the {a.title}.</span>
                          </label>
                          <div className="mt-4">
                            <SignaturePad testid={`sig-${a.key}`} defaultName={user?.name || ""}
                              onChange={(val) => setSig(a.key, val)} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <button data-testid="kyc-submit" onClick={submitInHouse} disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">
                  {submitting ? <><Loader2 className="animate-spin" size={15} /> Verifying…</> : <><Check size={15} /> Sign &amp; verify</>}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
